// ACTA_AGENT_CONTACT_CREATE_V2

import {
  prospects as prospectsApi,
} from "@/api";

import {
  normalizeAgentText,
  prospectDisplayName,
} from "@/agent/entityResolver";

const EMAIL_PATTERN =
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

function trimCapture(
  value = ""
) {
  return String(value)
    .trim()
    .replace(
      /^[,;:\s]+|[,;:\s]+$/g,
      ""
    );
}

// ACTA_AGENT_CONTACT_INPUT_CONFIDENCE_V1
function normalizeSpokenEmailPhrase(
  value = ""
) {
  return String(value)
    .normalize("NFD")
    .replace(
      /[\u0300-\u036f]/g,
      ""
    )
    .toLocaleLowerCase(
      "fr-FR"
    )
    .replace(
      /\btiret\s+du\s+bas\b/gu,
      "_"
    )
    .replace(
      /\bunderscore\b/gu,
      "_"
    )
    .replace(
      /\barobase\b/gu,
      "@"
    )
    .replace(
      /\bpoint\b/gu,
      "."
    )
    .replace(
      /\btiret\b/gu,
      "-"
    )
    .replace(
      /\s+/g,
      ""
    )
    .replace(
      /^[,;:]+|[,;:]+$/g,
      ""
    );
}

function extractEmail(
  command
) {
  const directMatch =
    String(command).match(
      /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i
    );

  if (directMatch?.[0]) {
    return directMatch[0];
  }

  const spokenMatch =
    String(command).match(
      /\b(?:adresse\s+)?(?:e-?mail|mail)\s*:?\s*([^,;]+?)(?=\s+(?:t[eé]l[eé]phone|t[eé]l|mobile|soci[eé]t[eé]|entreprise|chez)\b|[,;]|$)/iu
    );

  if (!spokenMatch?.[1]) {
    return "";
  }

  const candidate =
    normalizeSpokenEmailPhrase(
      spokenMatch[1]
    );

  return EMAIL_PATTERN.test(
    candidate
  )
    ? candidate
    : "";
}

function extractPhone(
  command
) {
  const candidates =
    String(command).match(
      /(?:\+?\d[\d\s().-]{6,}\d)/g
    ) || [];

  const valid =
    candidates
      .map(
        (value) =>
          trimCapture(value)
      )
      .filter(
        (value) =>
          value.replace(
            /\D/g,
            ""
          ).length >= 8
      )
      .sort(
        (left, right) =>
          right.replace(
            /\D/g,
            ""
          ).length
          - left.replace(
            /\D/g,
            ""
          ).length
      );

  return valid[0] || "";
}

function extractCompany(
  command
) {
  const match =
    String(command).match(
      /\b(?:nom\s+de\s+(?:la\s+)?soci[eé]t[eé]|soci[eé]t[eé]|entreprise|chez)\s*:?\s*([^,;]+?)(?=\s+(?:t[eé]l[eé]phone|t[eé]l|mobile|mail|email|e-mail)\b|[,;]|$)/iu
    );

  return trimCapture(
    match?.[1] || ""
  );
}

function extractName(
  command
) {
  const value =
    String(command);

  const withEntity =
    value.match(
      /\b(?:cr[eé]e|cr[eé]er|creer|ajoute|ajouter)\s+(?:(?:un|une)\s+)?(?:(?:nouveau|nouvelle)\s+)?(?:contact|prospect|client)\s+([A-Za-zÀ-ÿ'’-]{2,})\s+([A-Za-zÀ-ÿ'’-]{2,})/iu
    );

  if (withEntity) {
    return {
      firstName:
        trimCapture(
          withEntity[1]
        ),
      lastName:
        trimCapture(
          withEntity[2]
        ),
    };
  }

  const beforeCompany =
    value.match(
      /\b(?:cr[eé]e|cr[eé]er|creer|ajoute|ajouter)\s+([A-Za-zÀ-ÿ'’-]{2,})\s+([A-Za-zÀ-ÿ'’-]{2,})\s+(?:chez|soci[eé]t[eé]|entreprise)\b/iu
    );

  return {
    firstName:
      trimCapture(
        beforeCompany?.[1]
        || ""
      ),
    lastName:
      trimCapture(
        beforeCompany?.[2]
        || ""
      ),
  };
}

export function parseContactDraft(
  command
) {
  const name =
    extractName(command);

  return {
    firstName:
      name.firstName,
    lastName:
      name.lastName,
    company:
      extractCompany(
        command
      ),
    email:
      extractEmail(
        command
      ),
    phone:
      extractPhone(
        command
      ),
  };
}

export function validateContactDraft(
  draft
) {
  const firstName =
    String(
      draft?.firstName
      || ""
    ).trim();

  const lastName =
    String(
      draft?.lastName
      || ""
    ).trim();

  const company =
    String(
      draft?.company
      || ""
    ).trim();

  const email =
    String(
      draft?.email
      || ""
    ).trim();

  const phone =
    String(
      draft?.phone
      || ""
    ).trim();

  if (
    !firstName
    && !lastName
    && !company
  ) {
    return (
      "Renseignez au moins un prénom, un nom ou une société."
    );
  }

  if (
    email
    && !EMAIL_PATTERN.test(
      email
    )
  ) {
    return (
      "L’adresse email semble incorrecte."
    );
  }

  if (
    phone
    && phone.replace(
      /\D/g,
      ""
    ).length < 8
  ) {
    return (
      "Le numéro de téléphone semble incomplet."
    );
  }

  return "";
}

const COMMON_EMAIL_DOMAINS =
  new Set([
    "gmail",
    "outlook",
    "hotmail",
    "live",
    "icloud",
    "yahoo",
    "orange",
    "free",
    "wanadoo",
    "laposte",
    "protonmail",
    "proton",
  ]);

function companyIdentityToken(
  company = ""
) {
  const ignored =
    new Set([
      "sas",
      "sasu",
      "sarl",
      "eurl",
      "sa",
      "gmbh",
      "ltd",
      "inc",
      "france",
      "group",
      "groupe",
    ]);

  return normalizeAgentText(
    company
  )
    .split(" ")
    .filter(
      (token) =>
        token.length >= 2
        && !ignored.has(
          token
        )
    )[0]
    || "";
}

function emailDomainRoot(
  email = ""
) {
  const domain =
    String(email)
      .trim()
      .toLocaleLowerCase(
        "fr-FR"
      )
      .split("@")[1]
      || "";

  return domain
    .split(".")[0]
    .replace(
      /[^a-z0-9]/g,
      ""
    );
}

function sharedPrefixLength(
  left = "",
  right = ""
) {
  const max =
    Math.min(
      left.length,
      right.length
    );

  let length = 0;

  while (
    length < max
    && left[length] ===
      right[length]
  ) {
    length += 1;
  }

  return length;
}

export function contactDraftWarnings(
  draft
) {
  const warnings = {};

  const email =
    String(
      draft?.email
      || ""
    ).trim();

  const company =
    String(
      draft?.company
      || ""
    ).trim();

  if (
    email
    && company
    && EMAIL_PATTERN.test(
      email
    )
  ) {
    const companyToken =
      companyIdentityToken(
        company
      );

    const domainRoot =
      emailDomainRoot(
        email
      );

    const sameIdentity =
      companyToken
      && domainRoot
      && (
        companyToken ===
          domainRoot
        || domainRoot.startsWith(
          companyToken
        )
        || companyToken.startsWith(
          domainRoot
        )
      );

    const suspiciouslyClose =
      companyToken.length >= 4
      && domainRoot.length >= 4
      && !sameIdentity
      && !COMMON_EMAIL_DOMAINS.has(
        domainRoot
      )
      && sharedPrefixLength(
        companyToken,
        domainRoot
      ) >= 4;

    if (suspiciouslyClose) {
      warnings.email =
        `Le domaine « ${
          email.split("@")[1]
        } » ressemble au nom « ${
          company
        } » sans être identique. Vérifiez l’adresse reconnue.`;
    }
  }

  return warnings;
}

function normalizedPhone(
  value = ""
) {
  return String(value)
    .replace(/\D/g, "")
    .replace(
      /^0033/,
      "33"
    )
    .replace(
      /^0(?=\d{9}$)/,
      "33"
    );
}

function duplicateScore(
  prospect,
  draft
) {
  let score = 0;

  const email =
    normalizeAgentText(
      draft.email
    );

  const prospectEmail =
    normalizeAgentText(
      prospect?.email
    );

  if (
    email
    && prospectEmail
    && email === prospectEmail
  ) {
    score += 100;
  }

  const phone =
    normalizedPhone(
      draft.phone
    );

  const prospectPhone =
    normalizedPhone(
      prospect?.phone
      || prospect?.mobile
    );

  if (
    phone
    && prospectPhone
    && phone === prospectPhone
  ) {
    score += 100;
  }

  const name =
    normalizeAgentText(
      `${
        draft.firstName
        || ""
      } ${
        draft.lastName
        || ""
      }`
    );

  const prospectName =
    normalizeAgentText(
      prospectDisplayName(
        prospect
      )
    );

  if (
    name
    && prospectName
    && name === prospectName
  ) {
    score += 35;
  }

  const company =
    normalizeAgentText(
      draft.company
    );

  const prospectCompany =
    normalizeAgentText(
      prospect?.company
    );

  if (
    company
    && prospectCompany
    && company ===
      prospectCompany
  ) {
    score += 20;
  }

  return score;
}

export async function prepareContactCreate({
  command,
}) {
  const draft =
    parseContactDraft(
      command
    );

  const response =
    await prospectsApi.list();

  const prospects =
    Array.isArray(response)
      ? response
      : (
        response?.items
        || response?.prospects
        || []
      );

  const duplicates =
    prospects
      .map(
        (prospect) => ({
          prospect,
          score:
            duplicateScore(
              prospect,
              draft
            ),
        })
      )
      .filter(
        ({ score }) =>
          score >= 55
      )
      .sort(
        (left, right) =>
          right.score
          - left.score
      )
      .slice(0, 5)
      .map(
        ({ prospect }) =>
          prospect
      );

  return {
    draft,
    duplicates,
    validationError:
      validateContactDraft(
        draft
      ),
  };
}

export async function createContactFromDraft(
  draft
) {
  const validationError =
    validateContactDraft(
      draft
    );

  if (validationError) {
    throw new Error(
      validationError
    );
  }

  return prospectsApi.create({
    first_name:
      String(
        draft.firstName
        || ""
      ).trim(),
    last_name:
      String(
        draft.lastName
        || ""
      ).trim(),
    company:
      String(
        draft.company
        || ""
      ).trim(),
    email:
      String(
        draft.email
        || ""
      ).trim(),
    phone:
      String(
        draft.phone
        || ""
      ).trim(),
    city: "",
    country: "FR",
    commercial_status:
      "lead",
  });
}
